// AI Concierge streaming chat client (SSE over POST, so raw fetch — apiClient.request only returns parsed JSON)
import { getToken } from './apiClient';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.venuelista.com/api';

export interface ConciergeVenue {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  tags: string[];
  images?: string[];
  /** Display-only fallback location string, when address/city aren't populated. */
  location?: string | null;
}

export interface ConciergeMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface StreamConciergeChatArgs {
  message: string;
  history: ConciergeMessage[];
  character?: string | null;
  /** Transient browser geolocation — never persisted, just used for this search. */
  lat?: number | null;
  lng?: number | null;
  /** City selected via the /chat page's city picker — used to bias searches that don't
   *  name a location explicitly. Distinct from lat/lng: this is a deliberate user choice,
   *  not raw coordinates. */
  cityContext?: string | null;
}

interface StreamConciergeChatHandlers {
  onDelta: (text: string) => void;
  onVenues: (venues: ConciergeVenue[]) => void;
  onDone: () => void;
  onError: (message: string) => void;
}

/** Parses `data: {...}\n\n` SSE frames from a fetch ReadableStream (POST bodies can't use native EventSource). */
export async function streamConciergeChat(
  { message, history, character, lat, lng, cityContext }: StreamConciergeChatArgs,
  { onDelta, onVenues, onDone, onError }: StreamConciergeChatHandlers,
  signal?: AbortSignal
): Promise<void> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/concierge/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message, history, character, lat, lng, cityContext }),
      signal,
    });
  } catch {
    onError('Could not reach the concierge. Check your connection and try again.');
    return;
  }

  if (!response.ok || !response.body) {
    onError('The concierge is unavailable right now. Please try again.');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIndex: number;
      while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);

        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue;
          const json = line.slice(5).trim();
          if (!json) continue;

          let chunk: { type?: string; text?: string; venues?: ConciergeVenue[]; message?: string };
          try {
            chunk = JSON.parse(json);
          } catch {
            continue;
          }

          if (chunk.type === 'delta' && typeof chunk.text === 'string') {
            onDelta(chunk.text);
          } else if (chunk.type === 'venues' && Array.isArray(chunk.venues)) {
            onVenues(chunk.venues);
          } else if (chunk.type === 'error') {
            onError(chunk.message || 'Something went wrong.');
          } else if (chunk.type === 'done') {
            onDone();
            return;
          }
        }
      }
    }
    onDone();
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return;
    onError('The connection dropped. Please try again.');
  }
}

/** Loads the logged-in member's saved conversation so it can resume instead of restarting. Returns [] for guests. */
export async function fetchConciergeHistory(): Promise<ConciergeMessage[]> {
  const token = getToken();
  const headers: HeadersInit = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const response = await fetch(`${API_BASE_URL}/concierge/history`, { headers });
    if (!response.ok) return [];
    const data = (await response.json()) as { messages?: ConciergeMessage[] };
    return Array.isArray(data.messages) ? data.messages : [];
  } catch {
    return [];
  }
}
